<template>
  <nav class="breadcrumbs" data-test="search-breadcrumbs">
    <search-breadcrumbs-generator
      v-if="breadcrumbsInternal2"
      :breadcrumbs="breadcrumbsInternal2"
      :with-counts="withCounts"
      @update-rendered-url="({ label, url }) => updateRenderedUrl(label, url)"
    >
      <pu-link v-bind="homeBreadcrumb" exclude-type="fullMinusType">
        <translate>Home</translate>
      </pu-link>
      <span class="crumb">&rsaquo;</span>
    </search-breadcrumbs-generator>
    <nuxt-to-meta v-if="showSchema" :nuxt2meta="head" />
  </nav>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { Breadcrumb } from '~/apps/search/schemas'
import type { BreadcrumbCounts } from '~/composables/useBreadcrumbCounts'
import { useBreadcrumbCounts } from '~/composables/useBreadcrumbCounts'
import type { SearchFilters } from '~/types/search'
import { getFacetRules } from '~/utility/facets'
import { CATEGORIES } from '../../apps/pu-links/simpleCategories'
import { HomepageLinkGenerator } from '../../apps/url/generators/HomepageLinkGenerator'
import { GLAMPING_CATEGORIES, isGlamping } from '../../utility/glamping'
import { SimpleBreadcrumbWithCount } from '../search/types'

const props = defineProps<{
  breadcrumbs: Breadcrumb[]
  showSchema?: boolean
  counts?: Record<string, number>
  withCounts?: boolean
  linkAll?: boolean
}>()

const searchStore = useSearchStoreSetup(usePinia())
const route = useRoute()
const langCode = (route.params.lang as string) || 'en-gb'

const isGlampingCategory = computed(() => {
  return isGlamping(searchStore.searchFilters.categoryIds || [])
})

const isSeo = computed(() => {
  const searchFilters = searchStore.searchFilters
  // FIXED: Properly compare glamping categories using existing isGlamping helper
  const isGlampingCategories = isGlamping(searchFilters.categoryIds || [])

  // Non-SEO if has dates (arrival/departure)
  if (searchFilters.dates?.arrive || searchFilters.dates?.depart) {
    return false
  }

  // FIXED: Non-SEO only if has NON-DEFAULT party size params
  // Default party params (adults: 2, children: 0, infants: 0) should not affect SEO status
  const hasNonDefaultPartyParams =
    searchFilters.party &&
    ((searchFilters.party.adults !== undefined &&
      searchFilters.party.adults !== 2) ||
      (searchFilters.party.children !== undefined &&
        searchFilters.party.children !== 0) ||
      (searchFilters.party.infants !== undefined &&
        searchFilters.party.infants !== 0))

  if (hasNonDefaultPartyParams) {
    return false
  }

  // Non-SEO if has more than 1 facet
  if (searchFilters.facets?.length && searchFilters.facets.length > 1) {
    return false
  }

  // Non-SEO if has multiple categories (unless it's the glamping combination)
  if (searchFilters.categoryIds?.length > 1 && !isGlampingCategories) {
    return false
  }

  return true
})

const baseLangPrefix = computed(() => {
  return langCode === 'en-gb' ? '/' : `/${langCode}/`
})

function getBaseUrl(useSearch: boolean) {
  if (!isSeo.value || useSearch) {
    return `${baseLangPrefix.value}search`
  }
  if (searchStore.searchFilters.categoryIds?.length == 1) {
    const rootSlug = CATEGORIES[searchStore.searchFilters.categoryIds[0]]
    return `${baseLangPrefix.value}${rootSlug}`
  }
  if (isGlampingCategory.value) {
    return `${baseLangPrefix.value}glamping`
  }
  return `${baseLangPrefix.value}campsites`
}

const searchFilters = computed((): SearchFilters => {
  const filters = searchStore.searchFilters
  return {
    ...filters,
    hierarchyPath: filters.hierarchyPath ?? undefined,
  }
})

const breadcrumbCountsSearchFilters = computed(() => {
  return {
    ...searchFilters.value,
    facets: [],
  }
})

const hasFacets = computed(() => (searchFilters.value?.facets ?? []).length > 0)

const isDatedSearch = computed(() => {
  return Boolean(searchFilters.value?.dates?.arrive)
})

const { breadcrumbCounts } = useBreadcrumbCounts(
  breadcrumbCountsSearchFilters,
) as { breadcrumbCounts: Ref<BreadcrumbCounts> }

const { data: facetRules } = await useAsyncData(
  `search-breadcrumb-facet-rules-${JSON.stringify(searchFilters.value)}`,
  async () => {
    if (!searchFilters.value.facets?.length) {
      return []
    }
    const rules = await Promise.all(
      searchFilters.value.facets.map((facet) => getFacetRules(facet, langCode)),
    )
    return rules.flat()
  },
  {
    watch: [searchFilters],
    server: true,
    lazy: process.client,
  },
)

const hierarchyCounts = computed(
  () => breadcrumbCounts.value?.bookableFacetHierarchy ?? {},
)

const homeBreadcrumb = computed(() => {
  const homeLinkGenerator = new HomepageLinkGenerator()
  return homeLinkGenerator.getUrlData(searchFilters.value)
})

const breadcrumbsInternal2 = computed(() => {
  const newBreadcrumbs: SimpleBreadcrumbWithCount[] = []
  let removedFacetBreadcrumb: (typeof props.breadcrumbs)[0] | null = null

  props.breadcrumbs.forEach((breadcrumb, index) => {
    if (breadcrumb.name === 'Pitchup.com') {
      return
    }

    const hierarchyCount = breadcrumb.hierarchyPath
      ? hierarchyCounts.value[breadcrumb.hierarchyPath]
      : undefined

    const count = breadcrumb.hierarchyPath
      ? props.counts?.[breadcrumb.hierarchyPath]
      : undefined

    const countLevel = hasFacets.value ? 5 : 1
    const shouldRemove = facetRules.value?.some((rule) => {
      if (rule.category_all) {
        return true
      }
      if (rule.category_campsites && !searchFilters.value.categoryIds?.length) {
        return true
      }

      // FIXED: For glamping pages, only apply glamping-specific rules
      if (isGlampingCategory.value) {
        if (rule.category_glamping) {
          return true
        }
        // Skip individual category rules for glamping pages
        return false
      }

      // For non-glamping pages, check individual category matches
      const currentCategoryIds = searchFilters.value.categoryIds || []
      const categoryMatch = currentCategoryIds.some((id) =>
        rule.category_ids.includes(Number(id)),
      )
      if (categoryMatch) {
        return true
      }
      return false
    })

    // Check early return condition (facet breadcrumb at index 2)
    const shouldEarlyReturn = hasFacets.value && shouldRemove && index === 2

    if (shouldEarlyReturn) {
      // Track the removed facet breadcrumb so we can add it back later with correct URL
      removedFacetBreadcrumb = breadcrumb
      return
    }
    const linkable =
      typeof hierarchyCount === 'undefined' ||
      hierarchyCount >= countLevel ||
      typeof count === 'undefined'
    const url =
      breadcrumb.hierarchyPath === '/'
        ? shouldRemove || isDatedSearch.value
          ? `${getBaseUrl(true)}/`
          : breadcrumb.url
        : `${getBaseUrl(Boolean(shouldRemove) || isDatedSearch.value)}/${breadcrumb.hierarchyPath}`
    // FIXED: Always remove facet query from category root (index 1) when facets present
    // The facet now has its own tier in the breadcrumb, so the category root should never have ?facet=
    let removeFacetQuery = false
    if (index === 1 && hasFacets.value) {
      removeFacetQuery = true
    }

    newBreadcrumbs.push({
      label: breadcrumb.name,
      url: url,
      count,
      hierarchyLevel: breadcrumb.hierarchyLevel ?? undefined,
      hierarchyPath: breadcrumb.hierarchyPath ?? undefined,
      linkable,
      nonSeo: Boolean(shouldRemove) || isDatedSearch.value,
      to: { path: url },
      removeFacetQuery,
      debugData: {
        hierarchyCount,
        linkable,
        nonSeo: Boolean(shouldRemove) || isDatedSearch.value,
        hierarchyPath: breadcrumb.hierarchyPath,
        level: breadcrumb.hierarchyLevel,
      },
    })
  })

  // Add back the facet tier if it was removed
  if (removedFacetBreadcrumb && searchFilters.value.facets?.length === 1) {
    const facetSlug = searchFilters.value.facets[0]

    // Determine if we should use SEO URL or query param
    // Use ?facet= if category_all is true (would redirect)
    const useQueryParam = facetRules.value?.some(
      (rule) => rule.category_all === true,
    )

    // Build the facet breadcrumb URL
    const parentBreadcrumb = newBreadcrumbs[newBreadcrumbs.length - 1]
    const facetUrl = useQueryParam
      ? parentBreadcrumb.url // Will add ?facet= via query in SearchBreadcrumbsGenerator
      : `${getBaseUrl(false)}/-/${facetSlug}/`

    // Insert facet tier before the last breadcrumb (location)
    const lastBreadcrumb = newBreadcrumbs.pop()

    newBreadcrumbs.push({
      label: removedFacetBreadcrumb.name,
      url: facetUrl,
      count: undefined,
      hierarchyLevel: undefined,
      hierarchyPath: '/',
      linkable: true,
      nonSeo: useQueryParam,
      to: { path: facetUrl },
      removeFacetQuery: false,
      debugData: {
        hierarchyCount: undefined,
        linkable: true,
        nonSeo: useQueryParam,
        hierarchyPath: '/',
        level: undefined,
      },
    })

    // Add back the last breadcrumb (location)
    if (lastBreadcrumb) {
      newBreadcrumbs.push(lastBreadcrumb)
    }
  }

  return newBreadcrumbs
})

const renderedUrls = ref<Map<string, string>>(new Map())

const updateRenderedUrl = (label: string, url: string) => {
  renderedUrls.value.set(label, url)
}

const head = computed(() => {
  if (props.showSchema) {
    const allBreadcrumbs = [
      {
        label: 'Home',
        id: `https://${useRuntimeConfig().public.envUrl}${homeBreadcrumb.value.path}`,
      },
      ...breadcrumbsInternal2.value
        .filter(
          (crumb) =>
            crumb.linkable &&
            renderedUrls.value.has(crumb.label) &&
            crumb.hierarchyPath,
        )
        .map((crumb) => ({
          label: crumb.label,
          id: `https://${useRuntimeConfig().public.envUrl}${renderedUrls.value.get(crumb.label)}`,
        })),
    ]
    const lastBreadcrumb =
      breadcrumbsInternal2.value[breadcrumbsInternal2.value.length - 1]
    if (lastBreadcrumb && lastBreadcrumb.linkable) {
      // Check if there's a single facet and update the URL for the final breadcrumb
      let finalUrl = lastBreadcrumb.url
      const facets = searchStore.searchFilters.facets || []
      if (facets.length === 1 && isSeo.value) {
        const facetSlug = facets[0]
        // Check if we should use SEO URL (/-/facet/) or query param (?facet=)
        // Use query param if the hierarchy count is under 5
        const hierarchyCount = lastBreadcrumb.hierarchyPath
          ? hierarchyCounts.value[lastBreadcrumb.hierarchyPath]
          : undefined
        const shouldUseQueryParam =
          typeof hierarchyCount !== 'undefined' && hierarchyCount < 5

        if (shouldUseQueryParam) {
          // Use query parameter format
          const baseUrl = finalUrl.replace(/\/$/, '')
          finalUrl = `${baseUrl}?facet=${facetSlug}`
        } else if (!finalUrl.includes(`/-/${facetSlug}/`)) {
          // Use SEO URL format
          finalUrl = `${finalUrl.replace(/\/$/, '')}/-/${facetSlug}/`
        }
      }

      allBreadcrumbs.push({
        label: lastBreadcrumb.label,
        id: `https://${useRuntimeConfig().public.envUrl}${finalUrl}`,
      })
    }
    return {
      script: [
        {
          hid: 'ldjson-schema-search-breadcrumbs',
          type: 'application/ld+json',
          body: true,
          json: {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: allBreadcrumbs.map((breadcrumb, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              item: {
                '@id': breadcrumb.id,
                name: breadcrumb.label,
              },
            })),
          },
        },
      ],
    }
  }
  return {}
})
</script>

<style>
@import url('~/assets/css/breadcrumbs.css');
</style>
